import { Component, OnInit, inject } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { ProjectService } from '../project.service';
import { Revision } from '../../version-control/revision/revision.model';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import _ from 'lodash';
import { FileDownloader } from '../../common/helpers/file-downloader';
import { environment } from 'src/environments/environment';

@Component({
    selector: 'adm4-revert-project-component',
    template: `
    <adm4-modal-dialog-title header='Revert Project' [showClose]='true' (closeClicked)="closeDialog()">
      <div class='create-item-container'>
        <div class="adm4-form-group revert-project-warning-content">
          <span>
            <label class='input-label warning-title'>
              <span class='warning-icon-container'>
                <mat-icon class='warning-icon'>report_problem</mat-icon>
              </span>
              <span>
                Consider exporting the project <adm4-link (click)='exportProject()'>now</adm4-link>, since your local changes will be lost after reverting to a previous version.
                Please note that you will only load the previous configurations, no automatic deployment is done.
              </span>
            </label>
          </span>
        </div>
    
        <div class="adm4-form-group revert-project-target">
          <label class='input-label' for='project-revisions'>Select revision</label>
          <mat-form-field class='full-width'>
            <mat-select #revisionSelection
              id='project-revisions'
              [(ngModel)]='selectedRevision'
              [disableOptionCentering]="true"
              (click)='focusDropdownInput()'
              [placeholder]='placeholderMsg'>
              @if (revisionSelection.focused) {
                <adm4-searchable-dropdown-input
                  [sourceItems]='revisions'
                  [placeholder]="placeholderMsg"
                  [searchableFormatFn]='searchableRevisionFormatFn'
                  [focusTrigger]='searchableDropdownInputFocusTrigger$'
                (filteredResult)="updateSearchResult($event)"></adm4-searchable-dropdown-input>
              }
              @for (revision of revisions; track revision) {
                <mat-option [value]="revision" [hidden]='isRevisionFilteredOut(revision)' [title]='revision.message'>
                  {{revision?.committer?.when | date: commitDateFormat}} by {{revision.author.name}} ({{revision.message}}) @if (revision.tags && revision.tags.length > 0) {
                  @for (tag of revision.tags; track tag) {
                    <span>[{{tag}}] </span>
                  }
                }
              </mat-option>
            }
          </mat-select>
        </mat-form-field>
    
        @if (selectedRevision) {
          <div class="commit-details fade-white">
            <p><strong>Commit ID: </strong>{{selectedRevision.commitId}}</p>
            <p><strong>Publish comment: </strong>{{selectedRevision.message}}</p>
            @if (selectedRevision.tags && selectedRevision.tags.length > 0) {
              <p><strong>Tags: </strong>{{selectedRevision.tags.join(', ')}}</p>
            }
          </div>
        }
      </div>
    </div>
    <div mat-dialog-actions>
      <button type='button' class='admn4-button-text'
        (click)='closeDialog()'>
        Cancel
      </button>
      <button type='button' class='admn4-button-ellipse-blue'
              [disabled]='!selectedRevision'
              (click)='importRevision(projectKey, selectedRevision)'>
        Import as new
      </button>
      <button type='button' class='admn4-button-ellipse-blue'
        [disabled]='!selectedRevision'
        (click)='revertToRevision(projectKey, selectedRevision)'>
        Revert
      </button>
    </div>
    </adm4-modal-dialog-title>
    `,
    styleUrls: ['./revert-project.component.scss'],
    host: { '[class]': "'adm4-mat-dialog'" },
    standalone: false
})
export class RevertProjectComponent implements OnInit {
  private projectService = inject(ProjectService);
  projectKey = inject(MAT_DIALOG_DATA);
  private dialogRef = inject<MatDialogRef<RevertProjectComponent>>(MatDialogRef);


  _searchableDropdownInputFocusTrigger$: Subject<void> = new Subject<void>();
  searchableDropdownInputFocusTrigger$: Observable<void> = this._searchableDropdownInputFocusTrigger$.asObservable();
  selectedRevision: Revision;
  revisions: Revision[] | null;
  readonly commitDateFormat = 'dd.MM.yyyy HH:mm';
  private filteredRevisionList: Revision[];
  placeholderMsg= 'Please select a revision...';

  ngOnInit(): void {
    this.projectService.getProjectRevisionList(this.projectKey, 1000).subscribe(revision => {
      this.revisions = revision;
      if(this.hasEmptyRevisionList(revision)){
        this.placeholderMsg = 'The project has not been published yet. Only published projects can be reverted.';
      }
    });
  }

  focusDropdownInput(): void {
    this._searchableDropdownInputFocusTrigger$.next();
  }

  updateSearchResult(filteredList: Revision[]): void {
    this.filteredRevisionList = filteredList;
  }

  searchableRevisionFormatFn = (revision: Revision): string => {
    const tagsString = revision.tags ? revision.tags.join(' ') : '';
    return revision.message + revision.author.name + revision.author.when + revision.commitId + tagsString;
  };

  closeDialog() {
    this.dialogRef.close();
  }

  revertToRevision(projectKey: string, selectedRevision: Revision): void {
    this.dialogRef.close({projectKey: projectKey, selectedRevision:selectedRevision});
  }

  importRevision(projectKey: string, selectedRevision: Revision): void {
    this.dialogRef.close({projectKey: projectKey, selectedRevision: selectedRevision, importAsNew: true});
  }

  isRevisionFilteredOut(revision: Revision) {
    return !_.some(this.filteredRevisionList, value => _.isEqual(value, revision));
  }

  //todo: zvitkoczi fix this
  exportProject() {
    FileDownloader.downloadFileFromUrl(`${environment.baseUrl}/projects/${this.projectKey}/file-export`);
  }

  hasEmptyRevisionList(revisions: Revision[] | null): boolean {
    if (_.isNil(revisions)) {
      return false;
    }
    return _.isEmpty(revisions);
  }
}
